import styled, { css } from "styled-components";

// Overlay tints laid over each card's background image, plus the text colour
// used on that card's white pill button.
const TONES = {
  blue: {
    overlay: `linear-gradient(180deg,
      rgba(12, 35, 120, 0.1) 0%,
      rgba(18, 52, 170, 0.28) 36%,
      rgba(22, 64, 205, 0.9) 56%,
      rgba(29, 78, 216, 0.84) 100%)`,
    ink: "#1e3a8a",
  },
  green: {
    overlay: `linear-gradient(180deg,
      rgba(4, 40, 28, 0.1) 0%,
      rgba(6, 70, 50, 0.3) 36%,
      rgba(9, 92, 64, 0.9) 56%,
      rgba(36, 150, 104, 0.86) 100%)`,
    ink: "#14532d",
  },
  navy: {
    overlay: `linear-gradient(90deg,
      rgba(9, 16, 58, 0.98) 0%,
      rgba(11, 20, 68, 0.9) 36%,
      rgba(11, 20, 68, 0.4) 60%,
      rgba(11, 20, 68, 0) 80%)`,
    ink: "#1e2a78",
  },
};

const toneOf = (tone) => TONES[tone] || TONES.navy;

export const Wrapper = styled.div`
  max-width: 1080px;
  padding: 32px 20px 64px;
  margin: 0 auto;
  text-align: center;

  @media (max-width: 560px) {
    padding: 24px 16px 48px;
  }
`;

export const PageTitle = styled.h1`
  font-family: "Saira Semi Condensed", sans-serif;
  font-size: 36px;
  font-weight: 700;
  line-height: 1.1;
  color: #0f172a;
  margin: 0 0 6px;

  @media (max-width: 560px) {
    font-size: 30px;
  }
`;

export const PageSubtitle = styled.p`
  font-family: "Saira Semi Condensed", sans-serif;
  font-size: 16px;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 auto 24px;

  @media (max-width: 560px) {
    font-size: 15px;
    margin-bottom: 20px;
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    max-width: 520px;
    margin: 0 auto;
  }
`;

/* Cards */
const heroCard = css`
  min-height: 440px;
  padding: 200px 28px 24px;
  align-items: center;
  text-align: center;

  @media (max-width: 560px) {
    min-height: 0;
    padding: 180px 20px 24px;
  }
`;

const wideCard = css`
  min-height: 230px;
  padding: 28px 32px;
  justify-content: center;
  align-items: flex-start;
  text-align: left;

  & > * {
    max-width: 60%;
  }

  @media (max-width: 560px) {
    min-height: 220px;
    padding: 24px 20px;

    &::before {
      background: linear-gradient(90deg,
        rgba(9, 16, 58, 0.95) 0%,
        rgba(11, 20, 68, 0.78) 60%,
        rgba(11, 20, 68, 0.3) 100%);
    }

    & > * {
      max-width: 78%;
    }
  }
`;

export const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px;
  color: #ffffff;
  background-color: #0b1444;
  background-image: url(${({ $image }) => $image});
  background-size: cover;
  background-position: ${({ $position }) => $position || "center top"};
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.16);
  transition: box-shadow 0.25s ease;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: ${({ $tone }) => toneOf($tone).overlay};
    pointer-events: none;
  }

  & > * {
    position: relative;
  }

  @media (hover: hover) {
    &:hover {
      box-shadow: 0 18px 40px rgba(15, 23, 42, 0.24);
    }
  }

  ${({ $size }) => ($size === "wide" ? wideCard : heroCard)}
`;

export const CardTitle = styled.h2`
  font-family: "Saira Semi Condensed", sans-serif;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.1;
  color: #ffffff;
  margin: 0 0 4px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);

  @media (max-width: 560px) {
    font-size: 28px;
  }
`;

export const CardDesc = styled.p`
  font-family: "Saira Semi Condensed", sans-serif;
  font-size: 16px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.92);
  margin: 0;

  @media (max-width: 560px) {
    font-size: 15px;
  }
`;

export const CardActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: inherit;
  gap: 12px;
  width: 100%;
  margin-top: 18px;
`;

export const PillBtn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 46px;
  width: ${({ $block }) => ($block ? "min(300px, 100%)" : "auto")};
  padding: 0 28px;
  background: #ffffff;
  color: ${({ $tone }) => toneOf($tone).ink};
  border-radius: 999px;
  font-family: "Saira Semi Condensed", sans-serif;
  font-size: 19px;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  svg {
    width: 18px;
    height: 18px;
    transition: transform 0.2s ease;
  }

  &:hover {
    color: ${({ $tone }) => toneOf($tone).ink};
    transform: translateY(-1px);
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.24);

    svg {
      transform: translateX(3px);
    }
  }

  &:focus-visible {
    outline: 3px solid rgba(255, 255, 255, 0.8);
    outline-offset: 3px;
  }

  @media (max-width: 560px) {
    min-height: 44px;
    font-size: 18px;
  }
`;

export const TextLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.95);
  font-family: "Saira Semi Condensed", sans-serif;
  font-size: 15px;
  font-weight: 500;
  text-decoration: none;

  span {
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 4px;
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &:hover {
    color: #ffffff;

    span {
      text-decoration-thickness: 2px;
    }
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 3px;
    border-radius: 4px;
  }
`;

/* Multi-player join */
export const JoinForm = styled.form`
  display: flex;
  gap: 10px;
  width: 100%;
  max-width: 400px;
`;

export const JoinInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 40px;
  padding: 0 14px;
  background: #ffffff;
  border: 1px solid transparent;
  border-radius: 6px;
  font-family: "Saira Semi Condensed", sans-serif;
  font-size: 15px;
  color: #1f2937;
  outline: none;

  &::placeholder {
    color: #6b7280;
  }

  &:focus {
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.45);
  }
`;

export const JoinBtn = styled.button`
  height: 40px;
  padding: 0 24px;
  background: rgba(255, 255, 255, 0.06);
  color: #ffffff;
  border: 1.5px solid rgba(255, 255, 255, 0.9);
  border-radius: 6px;
  font-family: "Saira Semi Condensed", sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: #ffffff;
    color: ${TONES.green.ink};
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }
`;

export const ErrorText = styled.p`
  font-family: "Saira Semi Condensed", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #fee2e2;
  background: rgba(127, 29, 29, 0.55);
  padding: 4px 12px;
  border-radius: 6px;
  margin: 0;
`;

/* Modal */
export const ModalBody = styled.div`
  max-width: 466px;
  width: 100%;
  border-radius: 10px;
  background: #fff;
  padding: 32px 0;
  text-align: center;

  h2 {
    color: #0e1118;
    text-align: center;
    font-family: "Saira Semi Condensed";
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 41.6px;
    margin-bottom: 50px;
  }
`;

export const BtnWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  button {
    padding: 5.5px 24px;
    background-color: #004ea3;
    font-family: "Saira Semi Condensed", sans-serif;
    color: #ffffff;
    text-decoration: none;
    border-radius: 4px;
    font-weight: 400;
    font-size: 20px;
    line-height: 31px;
    opacity: 0.9;
    transition: all 0.3s ease;
    cursor: pointer;
    border: none;

    &:hover {
      opacity: 1;
    }
  }
`;
